import { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';

import { ConfigurableItemSearchModal } from '@erp/components/ConfigurableItemSearchModal';

interface AdvancedFiltersPayload {
  categoryIds: number[];
  sku?: string;
  tags: string[];
}

interface AdvancedItemSearchModalProps {
  isOpen: boolean;
  defaultQuery: string;
  defaultFilters: AdvancedFiltersPayload;
  onApply: (payload: { query: string; filters: AdvancedFiltersPayload }) => void;
  onClose: () => void;
}

export const AdvancedItemSearchModal: FC<AdvancedItemSearchModalProps> = ({
  isOpen,
  defaultQuery,
  defaultFilters,
  onApply,
  onClose,
}) => {
  const [query, setQuery] = useState(defaultQuery);
  const [categoryValues, setCategoryValues] = useState<unknown[]>(defaultFilters.categoryIds);
  const [categoryIds, setCategoryIds] = useState<number[]>(defaultFilters.categoryIds);
  const [sku, setSku] = useState(defaultFilters.sku ?? '');
  const [tagsInput, setTagsInput] = useState(defaultFilters.tags.join(', '));

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setQuery(defaultQuery);
    setCategoryValues(defaultFilters.categoryIds);
    setCategoryIds(defaultFilters.categoryIds);
    setSku(defaultFilters.sku ?? '');
    setTagsInput(defaultFilters.tags.join(', '));
  }, [defaultFilters.categoryIds, defaultFilters.sku, defaultFilters.tags, defaultQuery, isOpen]);

  const handleCategoryChange = useCallback((values: unknown[], expandedIds: number[]) => {
    setCategoryValues(values);
    setCategoryIds(expandedIds);
  }, []);

  const handleReset = useCallback(() => {
    setQuery('');
    setCategoryValues([]);
    setCategoryIds([]);
    setSku('');
    setTagsInput('');
  }, []);

  const handleApply = useCallback(() => {
    const tags = tagsInput
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    onApply({
      query: query.trim(),
      filters: {
        categoryIds,
        sku: sku.trim() ? sku.trim() : undefined,
        tags,
      },
    });
  }, [categoryIds, onApply, query, sku, tagsInput]);

  return (
    <ConfigurableItemSearchModal
      open={isOpen}
      title="Búsqueda avanzada"
      query={query}
      queryPlaceholder="Nombre, SKU o código de barras"
      selectedCategoryValues={categoryValues}
      categoryPopulationScope="items"
      textFilters={[
        {
          key: 'sku',
          label: 'SKU exacto',
          value: sku,
          placeholder: 'Ej: SERV-123',
          onChange: setSku,
        },
        {
          key: 'tags',
          label: 'Tags',
          value: tagsInput,
          placeholder: 'Ej: spa, promo',
          onChange: setTagsInput,
        },
      ]}
      resetDisabled={!query && categoryIds.length === 0 && !sku && !tagsInput}
      applyText="Aplicar filtros"
      onQueryChange={setQuery}
      onCategoryValuesChange={handleCategoryChange}
      onApply={handleApply}
      onReset={handleReset}
      onClose={onClose}
    />
  );
};

export default AdvancedItemSearchModal;
